import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Typography, Button, Box, Grid, ToggleButton, ToggleButtonGroup, Snackbar, Alert, Avatar } from '@mui/material';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import WarningIcon from '@mui/icons-material/Warning';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PermPhoneMsgIcon from '@mui/icons-material/PermPhoneMsg';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const alertOptions = [
  { key: 'wrong_parking', label: 'wrong_parking', icon: <ReportProblemIcon color="warning" /> },
  { key: 'emergency', label: 'emergency', icon: <WarningIcon color="error" /> },
  { key: 'lights_on', label: 'lights_on', icon: <LightbulbIcon color="primary" /> },
  { key: 'contact_owner', label: 'contact_owner', icon: <PermPhoneMsgIcon color="success" /> },
];

const maskPhone = (value) => {
  if (!value) return '-';
  const digits = value.replace(/\D/g, '');
  if (digits.length < 4) return 'xxxx';
  return `${digits.slice(0, 2)}xx xxxx${digits.slice(-2)}`;
};

function AlertPage({ vehicleId }) {
  const { t, i18n } = useTranslation();
  const [type, setType] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [alertError, setAlertError] = useState('');
  const [registration, setRegistration] = useState(null);
  const [loadingOwner, setLoadingOwner] = useState(true);
  const [ownerError, setOwnerError] = useState('');

  useEffect(() => {
    const fetchOwner = async () => {
      setLoadingOwner(true);
      setOwnerError('');
      try {
        const normalizedVehicleId = vehicleId?.trim().toUpperCase().replace(/\s+/g, '');
        const ownerQuery = query(
          collection(db, 'registrations'),
          where('vehicleNumber', '==', normalizedVehicleId)
        );
        const snapshot = await getDocs(ownerQuery);
        if (snapshot.empty) {
          setOwnerError('No owner found for this vehicle.');
          setRegistration(null);
        } else {
          const owner = snapshot.docs[0].data();
          setRegistration(owner);
        }
      } catch (err) {
        setOwnerError('Unable to load owner details.');
      } finally {
        setLoadingOwner(false);
      }
    };

    if (vehicleId) {
      fetchOwner();
    }
  }, [vehicleId]);

  const handleSubmit = async () => {
    if (!type || !registration) return;
    setSubmitting(true);
    setAlertError('');

    const sendAlertUrl = process.env.REACT_APP_SEND_ALERT_URL || 'https://us-central1-gadimitra-3d8e5.cloudfunctions.net/sendAlert';

    try {
      const response = await fetch(sendAlertUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vehicleId, type }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Alert API request failed (${response.status})`);
      }

      setSubmitted(true);
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Send alert failed:', err);
      setAlertError(err.message || 'Unable to send alert at this time. Please check your backend configuration and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLang = (event, lang) => {
    if (lang) i18n.changeLanguage(lang);
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
      <Card sx={{ maxWidth: 480, width: '100%', p: 2, boxShadow: 6 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <ToggleButtonGroup
              value={i18n.language}
              exclusive
              onChange={handleLang}
              size="small"
              color="primary"
            >
              <ToggleButton value="en">English</ToggleButton>
              <ToggleButton value="hi">हिन्दी</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: '#0b63f6' }}>
            GadiMitra – Vehicle Owner Alert System
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
            Instantly alert vehicle owners in Bihar! Scan the QR code on any vehicle to notify the owner about wrong parking, emergencies, or to contact them directly.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The vehicle number is encoded in the QR code, so there is no manual entry required.
          </Typography>

          {loadingOwner ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Loading owner information...
            </Typography>
          ) : ownerError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {ownerError}
            </Alert>
          ) : registration ? (
            <Box sx={{ mb: 3, p: 2, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Owner Details
              </Typography>
              <Typography variant="body2">Name: {registration.name}</Typography>
              <Typography variant="body2">Vehicle: {registration.vehicleNumber}</Typography>
              <Typography variant="body2">Contact: +91 {maskPhone(registration.contact)}</Typography>
              <Typography variant="body2">WhatsApp: +91 {maskPhone(registration.whatsapp)}</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Actual contact is protected. Press Send Alert to notify the owner internally.
              </Typography>
            </Box>
          ) : null}

          <Grid container spacing={2} sx={{ mt: 2, mb: 2 }}>
            {alertOptions.map(opt => (
              <Grid item xs={12} sm={6} key={opt.key}>
                <Button
                  fullWidth
                  variant={type === opt.key ? 'contained' : 'outlined'}
                  color="primary"
                  startIcon={opt.icon}
                  onClick={() => setType(opt.key)}
                  sx={{ py: 1.5, fontWeight: 600 }}
                >
                  {t(opt.label)}
                </Button>
              </Grid>
            ))}
          </Grid>
          {type && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                variant="contained"
                color="success"
                size="large"
                onClick={handleSubmit}
                disabled={!registration || submitting}
                sx={{ px: 5, py: 1.5 }}
              >
                {submitted ? t('alert_sent') : submitting ? t('sending_alert') || 'Sending...' : t('send_alert')}
              </Button>

              {!registration && (
                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                  Alert cannot be sent until owner details are available.
                </Typography>
              )}
            </Box>
          )}
          {alertError && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {alertError}
            </Alert>
          )}

          {registration && (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Owner number is protected. The alert will be sent internally.
              </Typography>
            </Box>
          )}

          {submitted && (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56, margin: 'auto', mb: 2 }}>
                <CheckCircleIcon fontSize="large" />
              </Avatar>
              <Typography variant="h6" color="success.main">{t('alert_sent')}</Typography>
            </Box>
          )}
        </CardContent>
        <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
            {t('alert_sent')}
          </Alert>
        </Snackbar>
      </Card>
    </Box>
  );
}

export default AlertPage;
